import React, { createContext, useContext, useState, useEffect } from 'react';
import { courtApi } from '../services/api';
import { useLiff } from '../hooks/useLiff';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false);
  const { liff, isInitialized, isLoading: liffLoading, error: liffError } = useLiff();

  // Test mechanism: Check if we should simulate token expiration
  const shouldSimulateTokenExpiration = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('test_token_expired') === 'true';
  };

  const fetchUserProfile = async () => {
    try {
      console.log('Fetching user profile...');
      const idToken = liff.getIDToken();
      
      if (!idToken) {
        console.log('No ID token available');
        return false;
      }

      // Test mechanism: Simulate token expiration if test parameter is present
      if (shouldSimulateTokenExpiration()) {
        console.log('🧪 TEST MODE: Simulating token expiration...');
        throw new Error('Token expired');
      }

      const userProfile = await courtApi.getUserProfile(idToken, liff);
      userProfile.idToken = idToken;
      setUser(userProfile);
      setError(null);
      return true;
    } catch (apiError) {
      console.error('API Error:', apiError);
      
      // Check if it's a token expired error
      const isTokenExpired = 
        apiError.error === 'Token expired' || 
        apiError.error_description === 'IdToken expired.' ||
        apiError.message?.includes('expired') ||
        apiError.status === 401;
      
      if (isTokenExpired) {
        console.log('Token expired, will trigger login...');
        setUser(null);
        setError(null);
        return false;
      }
      
      setError('API Error: ' + (apiError.error || apiError.message));
      setUser(null);
      return false;
    }
  };

  const handleLogin = async () => {
    try {
      console.log('Initiating login...');
      setHasAttemptedLogin(true);
      await liff.logout();
      await liff.login();
      // Page will redirect, so we don't need to do anything else here
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed: ' + err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeUser = async () => {
      // Wait for LIFF to be initialized
      if (liffLoading || !isInitialized) {
        return;
      }

      if (liffError) {
        setError('LIFF Error: ' + liffError);
        setLoading(false);
        return;
      }

      
      if (!liff.isLoggedIn()) {
        await handleLogin();
        setLoading(false);
        return;
      }

      // User is logged in, try to fetch profile
      const success = await fetchUserProfile();
      
      if (!success) {
        // Profile fetch failed (likely token expired), trigger login
        await handleLogin();
        return;
      }

      setLoading(false);
    };

    initializeUser();
  }, [liffLoading, liffError, isInitialized]);

  // Handle the case where we return from login redirect - more robust logic
  useEffect(() => {
    const handleReturnFromLogin = async () => {
      if (isInitialized && 
          !liffLoading && 
          !liffError && 
          liff.isLoggedIn() && 
          !user && 
          !loading) {
        try {
          await fetchUserProfile();
        } catch (error) {
          console.error('🔄 UserContext: Error fetching profile after login:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    handleReturnFromLogin();
  }, [isInitialized, liffLoading, liffError, user, loading]);

  // Handle page visibility changes (refresh, tab switch, etc.)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isInitialized && !liffLoading && !liffError && liff.isLoggedIn() && !user) {
        fetchUserProfile().catch(error => {
          console.error('🔄 UserContext: Error fetching profile on visibility change:', error);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isInitialized, liffLoading, liffError, user]);

  const updateUserContact = async (contactData) => {
    try {
      const idToken = liff.getIDToken();
      if (!idToken) {
        throw new Error('No ID token available');
      }

      const updatedProfile = await courtApi.updateUserContact(idToken, contactData, liff);
      setUser(prevUser => ({
        ...prevUser,
        ...updatedProfile
      }));
      return updatedProfile;
    } catch (err) {
      console.error('Error updating user contact:', err);
      throw err;
    }
  };

  const value = {
    user,
    loading: loading || liffLoading,
    error: error || liffError,
    isAuthenticated: !!user,
    refreshUserProfile: fetchUserProfile,
    updateUserContact,
    forceRefresh: () => {
      setUser(null);
      setError(null);
      setLoading(true);
      // This will trigger the useEffect to re-run
      setTimeout(() => {
        fetchUserProfile().finally(() => setLoading(false));
      }, 100);
    }
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}; 